
export interface RgbColor {
	blue: number;
	green: number;
	red: number;
};

export interface RgbaColor extends RgbColor {
    alpha: number;
}

/**
 * Returns an object with the rgb components of a hex color value.
 * @param hex - a hex color value (such as "#A1C2FF")
 */
export function hexToRgb(hex: string): RgbColor | null {
	const result = /^#?([a-f\d]?[a-f\d])([a-f\d]?[a-f\d])([a-f\d]?[a-f\d])$/i.exec(hex);
	if (result) {
		const repeatAmount = result[1].length < 2 ? 2 : 1;
		const red = parseInt(result[1].repeat(repeatAmount), 16);
		const green = parseInt(result[2].repeat(repeatAmount), 16);
		const blue = parseInt(result[3].repeat(repeatAmount), 16);
		return { red, green, blue };
	}
	return null;
}

/**
 * Returns an object with the rgba components of a hex color value.
 * @param hex - a hex color value (such as "#A1C2FF80")
 */
export function hexToRgba(hex: string): RgbaColor | null {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
	if (result) {
		const red = parseInt(result[1], 16);
		const green = parseInt(result[2], 16);
		const blue = parseInt(result[3], 16);
		let alpha = result[4] ? parseInt(result[4], 16) / 255 : 1; // Default to 1 if alpha is not provided
		alpha = parseFloat(alpha.toFixed(2));
		return { red, green, blue, alpha };
	}
	return null;
}

/**
 * Returns a hex value of an object with the rgb components of a color value.
 * @param rgb - an object with the { red, green, blue } components of a color value.
 */
export function rgbToHex(rgb: RgbColor | null): string {
	const { red = 0, green = 0, blue = 0 } = rgb || {};
	const hexValue = [red, green, blue]
		.map((colorComponent) => {
			const hex = (Number(colorComponent) || 0).toString(16);
			return `${hex.length === 1 ? "0" : ""}${hex}`;
  		})
		.join("");
	return `#${hexValue}`;
}

/**
 * Returns a hex value of an object with the rgb components of a color value.
 * @param rgba - an object with the { red, green, blue, alpha } components of a color value.
 */
export function rgbaToHex(rgba: RgbaColor | null): string {
	const { red = 0, green = 0, blue = 0, alpha= 1 } = rgba || {};
	const components = [red, green, blue, Math.round(alpha * 255)]; // Convert alpha to 0-255 scale
	const hexValue = components
		.map((component) => {
			const hex = (Number(component) || 0).toString(16);
			return `${hex.length === 1 ? "0" : ""}${hex}`;
		})
		.join("");
	return `#${hexValue}`;
}

/** 
 * Parse and return rga hex value and alpha values from a hex string.
 * @param hex - a hex color value (such as "#A1C2FF80")
 */
export function parseHexAlpha(hex: string): { rgb: string; alpha: number } {
	if (hex === "transparent") {
		return { rgb: "#000000", alpha: 0 };
	}
	const alphaHexValue = parseInt(hex.slice(7, 9), 16) / 255;
	const alpha = isNaN(alphaHexValue) ? 1 : Math.round(alphaHexValue * 100) / 100;
	const rgb = hex.slice(0, 7);
	return { rgb, alpha };
}

/** Combine a hex value and alpha value to create a hex string with alpha component. If alpha is 0 return Transparent 
 * @param hex - a hex color value (such as "#A1C2FF")
 * @param alpha - a number between 0 and 1
*/
export function combineHexAlpha(hex: string, alpha: number): string {
	if (alpha === 0) {
		return "transparent";
	}
	const alphaHexValue = Math.round(alpha * 255);
	const alphaHex = alphaHexValue.toString(16)
		.padStart(2, "0")
		.toUpperCase();
	return `${hex}${alphaHex}`;
}

